import { useEffect, useRef, useState } from "react";
import { Menu } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, streamMessage } from "@/lib/api";
import { findCategory, findModel } from "@/lib/meta";
import { ModelSwitcher } from "@/components/ModelSwitcher";
import { ThinkingToggle } from "@/components/ThinkingToggle";
import { MessageBubble, TypingBubble } from "@/components/MessageBubble";
import { Composer } from "@/components/Composer";

export const ChatView = ({ convId, catalog, onConversationChange, onOpenSidebar }) => {
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(null);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/conversations/${convId}`).then((r) => { setConv(r.data.conversation); setMessages(r.data.messages); })
      .catch((err) => toast.error(formatApiError(err)));
  }, [convId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async (content, referenceImage) => {
    setBusy(true);
    const temp = { id: `tmp-${Date.now()}`, role: "user", content, reference_preview: referenceImage };
    setMessages((m) => [...m, temp]);
    setStreaming("");
    let text = "";
    let images = [];
    try {
      await streamMessage(convId, { content, reference_image: referenceImage || undefined }, {
        user_message: (e) => setMessages((m) => m.map((x) => (x.id === temp.id ? { ...e.message, reference_preview: referenceImage } : x))),
        delta: (e) => { text += e.content; setStreaming(text); },
        image: (e) => { images = e.images; },
        error: (e) => { toast.error(e.detail || "Model error"); },
        done: (e) => {
          setMessages((m) => [...m, { ...e.message, elapsed: e.elapsed, images, prompt: content }]);
          if (e.title) { setConv((c) => ({ ...c, title: e.title })); onConversationChange({ id: convId, title: e.title, updated_at: new Date().toISOString() }); }
          else onConversationChange({ id: convId, updated_at: new Date().toISOString() });
        },
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStreaming(null);
      setBusy(false);
    }
  };

  const switchModel = async (provider, model) => {
    try {
      const { data } = await api.patch(`/conversations/${convId}`, { provider, model });
      setConv(data);
      onConversationChange(data);
      toast.success(`Switched to ${findModel(catalog, provider, model)?.name || model}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setThinking = async (thinking) => {
    try {
      const { data } = await api.patch(`/conversations/${convId}`, { thinking });
      setConv(data);
      toast.success(thinking ? "Deep thinking enabled — answers will be slower but more thorough" : "Deep thinking off");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const category = findCategory(catalog, conv?.category);

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="chat-view">
      <header className="h-16 shrink-0 flex items-center gap-3 px-4 lg:px-8 bg-[#090A0F]/80 backdrop-blur-xl border-b border-slate-800/60 z-10">
        <button onClick={onOpenSidebar} className="lg:hidden text-slate-400 hover:text-white" data-testid="open-sidebar-btn"><Menu className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-100 truncate" data-testid="chat-title">{conv?.title || "…"}</p>
          <p className="text-[11px] text-slate-500 truncate" data-testid="chat-category">{category?.label}</p>
        </div>
        {conv && <ThinkingToggle provider={conv.provider} model={conv.model} enabled={!!conv.thinking} onChange={setThinking} disabled={busy} />}
        {conv && catalog && <ModelSwitcher catalog={catalog} category={category} provider={conv.provider} model={conv.model} onSwitch={switchModel} disabled={busy} />}
      </header>

      <div className="flex-1 overflow-y-auto" data-testid="message-list">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8 space-y-6">
          {messages.length === 0 && !streaming && conv && (
            <div className="text-center py-20 rise" data-testid="chat-empty-state">
              <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">{category?.label}</p>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-100">Ask {findModel(catalog, conv.provider, conv.model)?.name} anything</h2>
              <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">{category?.description}</p>
            </div>
          )}
          {messages.map((m) => <MessageBubble key={m.id} message={m} catalog={catalog} />)}
          {streaming !== null && <TypingBubble text={streaming} catalog={catalog} provider={conv?.provider} model={conv?.model} isImage={category?.kind === "image"} thinking={!!conv?.thinking} />}
          <div ref={bottomRef} />
        </div>
      </div>

      <Composer onSend={send} disabled={busy || !conv} modelName={findModel(catalog, conv?.provider, conv?.model)?.name} isImage={category?.kind === "image"} allowReference={category?.kind === "image" && conv?.provider === "gemini"} />
    </div>
  );
};
